import { Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import MovieCard from "@/components/feature/MovieCard";
import NewsCard from "@/components/feature/NewsCard";
import SectionTitle from "@/components/feature/SectionTitle";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { allMovies, allNews, allSpecials, sortByPopularity } from "@/lib/content";
import HeroCarousel from "./components/HeroCarousel";
import CategoryEntry from "./components/CategoryEntry";
import SpecialBanner from "./components/SpecialBanner";

const HOME_TDK = {
  title: "国语对白做受69 - 影视资源与影视资讯平台",
  description:
    "国语对白做受69提供丰富多样的影视内容和最新影视资讯，涵盖电影、电视剧等多种类型，帮助用户轻松浏览和了解相关影视作品。",
  keywords: "国语对白做受69,影视资源,影视资讯,电影推荐,电视剧,影视平台",
};

export default function Home() {
  useDocumentMeta(HOME_TDK);

  const heroMovies = allMovies.filter((m) => m.backdrop).slice(0, 5);
  const featured = sortByPopularity(allMovies).slice(0, 8);
  const latestNews = allNews.slice(0, 5);
  const hotRank = sortByPopularity(allMovies).slice(0, 6);
  const spotlightSpecial = allSpecials[0];

  return (
    <Layout>
      <HeroCarousel items={heroMovies} />

      <div className="w-full max-w-[1400px] mx-auto px-4 md:px-8 py-10 md:py-14 space-y-12 md:space-y-16">
        <CategoryEntry />

        <section>
          <SectionTitle
            icon="ri-star-smile-line"
            title="最新影视资源精选"
            subtitle="口碑与热度兼备的影视作品，精选推荐"
            moreTo="/category/all"
            moreLabel="全部资源"
          />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
            {featured.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          <div className="lg:col-span-2">
            <SectionTitle
              icon="ri-newspaper-line"
              title="最新影视资讯"
              subtitle="行业动态、剧集资讯与影评推荐"
              moreTo="/news"
              moreLabel="更多资讯"
            />
            <div className="space-y-3">
              {latestNews.map((article) => (
                <NewsCard key={article.id} article={article} />
              ))}
            </div>
          </div>

          <aside>
            <SectionTitle icon="ri-fire-line" title="热播榜" subtitle="按热度排行" />
            <div className="rounded-lg border border-background-200 bg-background-50 divide-y divide-background-200 overflow-hidden">
              {hotRank.map((movie, index) => (
                <Link
                  key={movie.id}
                  to={`/detail/${movie.id}`}
                  className="group flex items-center gap-3 p-3 hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <span
                    className={`w-6 text-center font-bold ${
                      index < 3 ? "text-accent-600" : "text-foreground-400"
                    }`}
                  >
                    {index + 1}
                  </span>
                  <span className="w-10 h-14 shrink-0 rounded-md overflow-hidden bg-background-100">
                    <img
                      src={movie.poster}
                      alt={movie.title}
                      title={`${movie.title} - 热门影视`}
                      className="w-full h-full object-cover object-top"
                    />
                  </span>
                  <div className="min-w-0 flex-1">
                    <h4 className="text-sm font-medium text-foreground-900 truncate group-hover:text-primary-600 transition-colors">
                      {movie.title}
                    </h4>
                    <p className="mt-0.5 text-xs text-foreground-500 truncate">
                      {movie.year} · {movie.genres[0]}
                    </p>
                  </div>
                  <span className="text-sm font-semibold text-accent-600">{movie.rating.toFixed(1)}</span>
                </Link>
              ))}
            </div>
          </aside>
        </section>

        <section>
          <SectionTitle
            icon="ri-bookmark-3-line"
            title="主题专题"
            subtitle="编辑精选的影视内容合集"
            moreTo="/special"
            moreLabel="全部专题"
          />
          {spotlightSpecial && <SpecialBanner special={spotlightSpecial} />}
        </section>

        <section className="rounded-lg bg-secondary-50 border border-secondary-200 p-6 md:p-10">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-1.5 text-secondary-700 text-sm font-medium mb-3">
              <i className="ri-information-line"></i>
              关于本站
            </span>
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading">
              国语对白做受69 · 影视资源与影视资讯平台
            </h2>
            <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed">
              我们致力于为影视爱好者提供清晰、舒适的内容浏览体验。在这里，你可以按电影、电视剧分类快速查找作品，
              查看剧情简介、主创信息与相关推荐，也能第一时间了解最新影视资讯与主题专题，
              让每一次选择都更高效、更值得期待。
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Link
                to="/category/movie"
                className="px-5 py-2.5 rounded-md bg-primary-600 text-background-50 text-sm font-medium hover:bg-primary-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                浏览电影
              </Link>
              <Link
                to="/about"
                className="px-5 py-2.5 rounded-md bg-background-50 text-foreground-800 border border-background-300 text-sm font-medium hover:border-accent-400 transition-colors whitespace-nowrap cursor-pointer"
              >
                了解我们
              </Link>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}